/**
 * Utilities for cleaning up markdown content during streaming.
 * Handles incomplete constructs that would render incorrectly mid-stream.
 */
import { convertReferencesToMarkdown } from "../marvin/referenceProjection";

/**
 * Remove incomplete references from the end of content.
 * References look like {31} - we need to hide partial ones like {3 until complete.
 */
export function removeIncompleteReferences(content: string): string {
    // Match an incomplete reference at the end: { followed by optional digits, not closed
    // This handles cases like "{", "{3", "{31" at the very end
    return content.replace(/\{[\d]*$/, "");
}

/**
 * Check if a line is a table separator row (e.g., |---|---|---|)
 */
export function isTableSeparator(line: string): boolean {
    const trimmed = line.trim();
    return /^\|[\s:|-]+\|$/.test(trimmed) && trimmed.includes("-");
}

/**
 * Check if a line is a complete table row (starts and ends with |)
 */
export function isCompleteTableRow(line: string): boolean {
    const trimmed = line.trim();
    return trimmed.startsWith("|") && trimmed.endsWith("|");
}

/**
 * Remove incomplete tables from the end of markdown content.
 * A table is incomplete if:
 * - Any row starts with | but doesn't end with |
 * - We have a header row but no complete separator row yet
 * - We have header + separator but no data rows yet
 */
export function removeIncompleteTables(content: string): string {
    const lines = content.split("\n");
    if (lines.length === 0) return content;

    // Find where the table starts (walk backwards from end)
    let tableStartIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.startsWith("|")) {
            tableStartIndex = i;
        } else if (tableStartIndex !== -1) {
            // Found non-table line, table starts at tableStartIndex
            break;
        }
    }

    // No table at the end
    if (tableStartIndex === -1) return content;

    // Extract table lines
    const tableLines = lines.slice(tableStartIndex);

    // Check if any row is incomplete (starts with | but doesn't end with |)
    const hasIncompleteRow = tableLines.some(
        (line) => line.trim().startsWith("|") && !line.trim().endsWith("|"),
    );
    if (hasIncompleteRow) {
        // Remove the entire table, even if earlier rows are complete.
        // Partial tables cause layout instability during streaming.
        return lines.slice(0, tableStartIndex).join("\n");
    }

    // Check table structure: need header, separator, and at least one data row
    const completeRows = tableLines.filter((line) => isCompleteTableRow(line));
    const separatorIndex = completeRows.findIndex((line) => isTableSeparator(line));

    // No separator yet - table is incomplete
    if (separatorIndex === -1) {
        return lines.slice(0, tableStartIndex).join("\n");
    }

    // Need at least: header (before separator) + separator + 1 data row
    // So we need at least 3 complete rows, with separator at index >= 1 (header before it)
    // and separator not being the last row (data after it)
    if (
        completeRows.length < 3 ||
        separatorIndex < 1 ||
        separatorIndex === completeRows.length - 1
    ) {
        return lines.slice(0, tableStartIndex).join("\n");
    }

    return content;
}

/**
 * Fix table separator rows that have a different column count than the header.
 * LLMs sometimes miscount columns in wide tables (e.g., 25-column Gantt charts),
 * producing a separator with fewer dashes than the header has columns.
 * remark-gfm requires the separator to match the header to recognize a table.
 */
function fixTableSeparators(content: string): string {
    const lines = content.split("\n");

    for (let i = 1; i < lines.length; i++) {
        if (!isTableSeparator(lines[i])) continue;

        const headerLine = lines[i - 1].trim();
        if (!headerLine.startsWith("|") || !headerLine.endsWith("|")) continue;

        const headerCols = (headerLine.match(/\|/g) || []).length - 1;
        const sepCols = (lines[i].trim().match(/\|/g) || []).length - 1;

        if (sepCols !== headerCols) {
            lines[i] = "|" + Array(headerCols).fill("---").join("|") + "|";
        }
    }

    return lines.join("\n");
}

/** Inline markdown token rendered as an animated cursor by MarkdownRenderer. */
export const STREAMING_CURSOR_TOKEN = "[●](#streaming-cursor)";

/**
 * Clean up markdown content for rendering during streaming.
 * Removes incomplete references and tables to prevent rendering artifacts.
 */
export function getCompleteMarkdown(content: string): string {
    let result = content;

    // Remove any incomplete reference at the end
    result = removeIncompleteReferences(result);

    // Remove incomplete tables
    result = removeIncompleteTables(result);

    return result;
}

/**
 * Full markdown preprocessing chain for a Marvin (or user) message, shared by
 * the web `MarvinMessage` renderer and the headless native `ConversationEngine`
 * (`native/conversationEngine.ts`): trims incomplete reference/table constructs
 * while streaming, then fixes malformed
 * table separators and converts `{12}`-style reference tokens into markdown
 * links. Callers still run the result through `splitMarkdownByWidgets`
 * themselves — `MarvinMessage` does so after applying its own high-water-mark
 * smoothing (a React-only concern to stop content "shrinking" mid-stream);
 * the engine does so immediately since it has no rendering to smooth.
 */
export function preprocessMessageMarkdown(content: string, isStreaming: boolean): string {
    const cleanedContent = isStreaming ? getCompleteMarkdown(content) : content;
    return convertReferencesToMarkdown(fixTableSeparators(cleanedContent));
}
